import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import Button from '../components/ui/Button';
import SegmentedControl from '../components/ui/SegmentedControl';
import { EmptyState, ErrorState } from '../components/ui/States';
import { fetchInboxCounts } from '../lib/inboxApi';
import { fetchRfqList } from '../lib/rfqApi';
import { fetchQuotationList } from '../lib/quotationApi';
import { fetchConfirmedOrders } from '../lib/confirmedOrderApi';
import { listVendorInvoices } from '../lib/vendorInvoiceApi';
import {
  listAccountsPayables,
  listAccountsPayablePayments,
} from '../lib/accountsPayableApi';
import { getInventoryItems } from '../lib/inventoryApi';
import { SourceTracePanel } from '../features/traceability/SourceTracePanel';
import { formatDate as fmtDate, formatDateTime as fmtDateTime } from "../utils/dateFormat";
import { downloadObjectsCsv } from '../utils/downloadCsv';
import { quotationVesselName } from '../utils/quotationVessel';

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmt(value?: number, currency?: string) {
  if (value == null || Number.isNaN(value)) return '--';
  if (currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return `${currency} ${value.toFixed(2)}`;
    }
  }
  return value.toLocaleString();
}



// Kept as a local name because this page calls it eleven times; the escaping and
// the download are the owners' now. The private `escape` it replaced doubled
// quotes and did nothing about formulas — see utils/csv. (TECH_DEBT #38)
const downloadCsv = downloadObjectsCsv;

// ─── tiny UI pieces ───────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  color = 'default',
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  color?: 'default' | 'cyan' | 'amber' | 'red' | 'purple' | 'sky';
}) {
  const border = {
    default: 'border-slate-200',
    cyan: 'border-cyan-200',
    amber: 'border-amber-200',
    red: 'border-red-200',
    purple: 'border-purple-200',
    sky: 'border-sky-200',
  }[color];
  const text = {
    default: 'text-slate-900',
    cyan: 'text-cyan-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
    purple: 'text-purple-700',
    sky: 'text-sky-700',
  }[color];
  return (
    <div className={`rounded-xl bg-white border ${border} p-4 flex flex-col gap-1`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-xl font-bold tabular-nums ${text}`}>{value ?? '--'}</div>
      {sub && <div className="text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

function SectionHeader({
  title,
  count,
  onDownload,
  loading,
}: {
  title: string;
  count?: number;
  onDownload: () => void;
  loading?: boolean;
}) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {count != null && (
          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-xs font-semibold tabular-nums text-slate-500">
            {count}
          </span>
        )}
        {loading && (
          <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin motion-reduce:animate-none text-primary-500" />
        )}
      </div>
      <Button size="sm" onClick={onDownload} icon={<Download aria-hidden="true" className="h-4 w-4" />}>
        Export CSV
      </Button>
    </div>
  );
}

function SimpleTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: (string | number | React.ReactNode)[][];
}) {
  return (
    <div className="overflow-auto rounded-lg border border-slate-200">
      {/* `data-table` (index.css) draws the header, the cells and the row hover;
          `data-table-dense` because these are breakdown grids read in bulk. */}
      <table className="data-table data-table-dense">
        <thead className="sticky top-0">
          <tr>
            {headers.map((h) => (
              <th key={h} className="whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={headers.length} className="py-8">
                <EmptyState title="No data available." />
              </td>
            </tr>
          )}
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} className="whitespace-nowrap">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── TABS ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'rfq', label: 'RFQ & Inbox' },
  { id: 'quotations', label: 'Quotations' },
  { id: 'confirmed', label: 'Confirmed Orders' },
  { id: 'vendor_invoices', label: 'Vendor Invoices' },
  { id: 'ap', label: 'Accounts Payable' },
  // Vendor Source Traceability — reverse trace from a supplier back to vessels.
  { id: 'trace', label: 'Source Trace' },
] as const;

type TabId = (typeof TABS)[number]['id'];

const TAB_IDS = TABS.map((t) => t.id) as TabId[];

// ClickUp 86d42zz72: the sub-tab lived only in React state, so a refresh always
// landed back on Overview regardless of which report the user had open. Read
// once at mount, lazily, so the very first render already reflects it.
function initialReportsTabFromUrl(): TabId {
  const qs = new URLSearchParams(window.location.search);
  const requested = qs.get('tab') as TabId | null;
  return requested && (TAB_IDS as string[]).includes(requested) ? requested : 'overview';
}

// ─── PAGE ─────────────────────────────────────────────────────────────────────

export const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState<TabId>(initialReportsTabFromUrl);

  const handleTabChange = (id: TabId) => {
    setTab(id);
    navigate(`/reports?tab=${id}`, { replace: true });
  };

  // Keeps the tab in sync with browser back/forward, not just the initial load.
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const requested = qs.get('tab') as TabId | null;
    const next = requested && (TAB_IDS as string[]).includes(requested) ? requested : 'overview';
    setTab((prev) => (prev === next ? prev : next));
  }, [location.search]);
  const [loading, setLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  // Every source is fetched with allSettled and each rejection used to be
  // dropped on the floor, so a module that failed to answer showed as 0 or '--'
  // — indistinguishable from a module with nothing in it. A report that quietly
  // under-counts is worse than one that says it is incomplete.
  const [failedModules, setFailedModules] = useState<string[]>([]);

  // raw data
  const [inboxCounts, setInboxCounts] = useState<any>(null);
  const [rfqData, setRfqData] = useState<any>(null);
  const [quotations, setQuotations] = useState<any[]>([]);
  const [confirmedOrders, setConfirmedOrders] = useState<any[]>([]);
  const [vendorInvoiceStats, setVendorInvoiceStats] = useState<any>(null);
  const [apSummary, setApSummary] = useState<any>(null);
  const [apPaymentSummary, setApPaymentSummary] = useState<any>(null);
  const [inventoryTotal, setInventoryTotal] = useState<number>(0);
  const [inventoryLowStock, setInventoryLowStock] = useState<number>(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [
        inboxRes,
        rfqRes,
        quoteRes,
        confirmedRes,
        viRes,
        apRes,
        apPayRes,
      ] = await Promise.allSettled([
        fetchInboxCounts(),
        fetchRfqList('all', 1, 200),
        fetchQuotationList({ page: 1, limit: 200 }),
        fetchConfirmedOrders(1, 200),
        listVendorInvoices({ page: 1, limit: 1 }),
        listAccountsPayables({ page: 1, limit: 1, status: 'all' }),
        listAccountsPayablePayments({ page: 1, limit: 1, status: 'applied' }),
      ]);

      const failed: string[] = [];
      const note = (name: string, res: PromiseSettledResult<unknown>) => {
        if (res.status === 'rejected') failed.push(name);
      };
      note('Inbox', inboxRes);
      note('RFQ', rfqRes);
      note('Quotations', quoteRes);
      note('Confirmed Orders', confirmedRes);
      note('Vendor Invoices', viRes);
      note('Accounts Payable', apRes);
      note('Payments', apPayRes);

      if (inboxRes.status === 'fulfilled') setInboxCounts(inboxRes.value);
      if (rfqRes.status === 'fulfilled') setRfqData(rfqRes.value);
      if (quoteRes.status === 'fulfilled') setQuotations((quoteRes.value as any)?.data?.quotations || []);
      if (confirmedRes.status === 'fulfilled') setConfirmedOrders(confirmedRes.value.confirmedOrders || []);
      if (viRes.status === 'fulfilled') {
        // listVendorInvoices returns { items, total, stats } directly (no .data wrapper)
        const d = viRes.value as any;
        setVendorInvoiceStats({ total: d?.total ?? 0, stats: d?.stats ?? {} });
      }
      if (apRes.status === 'fulfilled') {
        // listAccountsPayables returns { success, data: { summary, items, ... } }
        setApSummary((apRes.value as any).data?.summary ?? null);
      }
      if (apPayRes.status === 'fulfilled') {
        setApPaymentSummary((apPayRes.value as any).data?.summary ?? null);
      }

      // inventory
      try {
        const invRes = await getInventoryItems({ page: 1, limit: 1 });
        setInventoryTotal(invRes.total ?? 0);
        const lowRes = await getInventoryItems({ lowStockOnly: true, page: 1, limit: 1 });
        setInventoryLowStock(lowRes.total ?? 0);
      } catch {
        // Was a bare swallow, which is how a failed stock count printed as zero
        // SKUs. It is reported with the rest now.
        failed.push('Inventory');
      }

      setFailedModules(failed);
      setLastRefreshed(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // ── derived ──────────────────────────────────────────────────────────────
  const rfqStatusCounts = useMemo(() => {
    const sc = rfqData?.statusCounts || {};
    return {
      total: rfqData?.total ?? 0,
      pending: sc.pending ?? sc.new ?? 0,
      processed: sc.processed ?? sc.ready ?? 0,
      error: sc.error ?? 0,
    };
  }, [rfqData]);

  const quotationStats = useMemo(() => {
    const total = quotations.length;
    const totalValue = quotations.reduce((s, q) => s + (q.summary?.totalValue || 0), 0);
    const byStatus: Record<string, number> = {};
    quotations.forEach((q) => {
      const s = q.status || 'unknown';
      byStatus[s] = (byStatus[s] || 0) + 1;
    });
    return { total, totalValue, avgValue: total ? totalValue / total : 0, byStatus };
  }, [quotations]);

  const confirmedStats = useMemo(() => {
    const total = confirmedOrders.length;
    const totalRevenue = confirmedOrders.reduce((s, o) => s + (o.totalQuote || 0), 0);
    const totalItems = confirmedOrders.reduce((s, o) => s + (o.totalItems || 0), 0);
    return { total, totalRevenue, totalItems, avgOrder: total ? totalRevenue / total : 0 };
  }, [confirmedOrders]);

  // ── CSV generators ────────────────────────────────────────────────────────
  const exportOverview = () => {
    downloadCsv('report_overview.csv', [
      { Module: 'RFQ', 'Total Records': rfqStatusCounts.total, Processed: rfqStatusCounts.processed, Pending: rfqStatusCounts.pending, Error: rfqStatusCounts.error },
      { Module: 'Quotations', 'Total Records': quotationStats.total, 'Total Value (USD)': quotationStats.totalValue.toFixed(2), 'Avg Value': quotationStats.avgValue.toFixed(2) },
      { Module: 'Confirmed Orders', 'Total Records': confirmedStats.total, 'Total Revenue (USD)': confirmedStats.totalRevenue.toFixed(2) },
      { Module: 'Vendor Invoices', 'Total Records': vendorInvoiceStats?.total ?? 0 },
      { Module: 'Accounts Payable', 'Total Invoiced': apSummary?.totalAmount ?? 0, 'Paid': apSummary?.paidAmount ?? 0, 'Outstanding': apSummary?.outstandingAmount ?? 0 },
      { Module: 'Inventory', 'Total SKUs': inventoryTotal, 'Low Stock': inventoryLowStock },
    ]);
  };

  const exportRfq = () => {
    const rows = (rfqData?.data || []).map((r: any) => ({
      'RFQ ID': r.rfqId || r._id,
      Client: r.customerName || '',
      Vessel: r.vesselName || '',
      Status: r.status || '',
      'Item Count': r.itemCount || 0,
      'Received At': fmtDateTime(r.receivedAt),
    }));
    downloadCsv('report_rfq.csv', rows.length ? rows : [{ Note: 'No RFQ data' }]);
  };

  const exportQuotations = () => {
    const rows = quotations.map((q) => ({
      'Quotation Number': q.quotationNumber || '',
      Client: q.customer?.customerName || '',
      Vessel: quotationVesselName(q) || '',
      Status: q.status || '',
      'Total Value (USD)': q.summary?.totalValue ?? '',
      'Total Items': q.summary?.totalItems ?? '',
      'Created At': fmtDateTime(q.createdAt),
    }));
    downloadCsv('report_quotations.csv', rows.length ? rows : [{ Note: 'No quotation data' }]);
  };

  const exportConfirmed = () => {
    const rows = confirmedOrders.map((o) => ({
      Client: o.clientName || '',
      Vessel: o.vesselName || '',
      'Total Quote': o.totalQuote ?? '',
      Currency: o.currency || '',
      'Total Items': o.totalItems ?? '',
      Status: o.status || '',
      'Confirmed At': fmtDateTime(o.confirmedAt),
    }));
    downloadCsv('report_confirmed_orders.csv', rows.length ? rows : [{ Note: 'No confirmed order data' }]);
  };

  const exportVendorInvoices = () => {
    const stats = vendorInvoiceStats?.stats || {};
    const rows = Object.entries(stats).map(([status, count]) => ({ Status: status, Count: count }));
    rows.push({ Status: 'TOTAL', Count: vendorInvoiceStats?.total ?? 0 });
    downloadCsv('report_vendor_invoices.csv', rows);
  };

  const exportAP = () => {
    if (!apSummary) return;
    downloadCsv('report_accounts_payable.csv', [
      { Metric: 'Total Invoices', Value: apSummary.invoiceCount },
      { Metric: 'Total Amount', Value: apSummary.totalAmount },
      { Metric: 'Paid Amount', Value: apSummary.paidAmount },
      { Metric: 'Outstanding Amount', Value: apSummary.outstandingAmount },
      { Metric: 'Unpaid Count', Value: apSummary.unpaidCount },
      { Metric: 'Partially Paid Count', Value: apSummary.partialCount },
      { Metric: 'Paid Count', Value: apSummary.paidCount },
      { Metric: 'Overdue Count', Value: apSummary.overdueCount },
      { Metric: 'Payment Vouchers', Value: apPaymentSummary?.paymentCount ?? 0 },
      { Metric: 'Total Payments Made', Value: apPaymentSummary?.totalAmount ?? 0 },
    ]);
  };

  // ── render helpers ────────────────────────────────────────────────────────
  const renderOverview = () => (
    <div className="space-y-6">
      <SectionHeader title="Business Overview" onDownload={exportOverview} loading={loading} />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatCard label="Total RFQs" value={rfqStatusCounts.total} sub={`${rfqStatusCounts.processed} processed`} color="cyan" />
        <StatCard label="Quotations" value={quotationStats.total} sub={`${fmt(quotationStats.totalValue, 'USD')} total`} color="sky" />
        <StatCard label="Confirmed Orders" value={confirmedStats.total} sub={`${fmt(confirmedStats.totalRevenue, 'USD')} revenue`} color="cyan" />
        <StatCard label="Vendor Invoices" value={vendorInvoiceStats?.total ?? '--'} sub={`${vendorInvoiceStats?.stats?.done ?? 0} done`} color="default" />
        <StatCard label="AP Outstanding" value={fmt(apSummary?.outstandingAmount, 'TTD')} sub={`${apSummary?.overdueCount ?? 0} overdue`} color="amber" />
        <StatCard label="Inventory SKUs" value={inventoryTotal || '--'} sub={`${inventoryLowStock} low stock`} color="purple" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* RFQ funnel */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs font-semibold text-slate-700 mb-3">RFQ pipeline</div>
          <div className="space-y-2">
            {[
              // Conversations, not emails, and not 'unclassified' — this is the All tab's
              // badge, which counts distinct threads matching the base inbox query. It was
              // relabelled, not repointed: no other figure here is the right one either, and
              // the rows below it are RFQ documents and quotations, so the funnel already
              // mixes units. Saying which unit each row is in is the honest minimum.
              // TECH_DEBT #47.
              { label: 'Conversations received', val: inboxCounts?.inbox ?? 0, color: 'bg-slate-400' },
              { label: 'Classified as RFQ', val: inboxCounts?.rfq ?? rfqStatusCounts.total, color: 'bg-cyan-400' },
              { label: 'Quotations Generated', val: quotationStats.total, color: 'bg-sky-400' },
              { label: 'Confirmed Orders', val: confirmedStats.total, color: 'bg-blue-500' },
            ].map(({ label, val, color }) => (
              <div key={label} className="flex items-center gap-3">
                <div className="w-28 text-xs text-slate-500 shrink-0">{label}</div>
                <div className="flex-1 bg-slate-100 rounded-full h-2">
                  <div
                    className={`${color} h-2 rounded-full transition-all`}
                    style={{ width: `${Math.min(100, ((val || 0) / Math.max(inboxCounts?.inbox || 1, rfqStatusCounts.total || 1, 1)) * 100)}%` }}
                  />
                </div>
                <div className="w-8 text-right text-xs font-semibold tabular-nums text-slate-700">{val}</div>
              </div>
            ))}
          </div>
        </div>

        {/* AP snapshot */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs font-semibold text-slate-700 mb-3">AP financial snapshot</div>
          {apSummary ? (
            <div className="space-y-2">
              {[
                { label: 'Total Invoiced', value: fmt(apSummary.totalAmount, 'TTD'), color: 'text-slate-700' },
                { label: 'Paid', value: fmt(apSummary.paidAmount, 'TTD'), color: 'text-cyan-600' },
                { label: 'Outstanding', value: fmt(apSummary.outstandingAmount, 'TTD'), color: 'text-amber-600' },
                { label: 'Overdue invoices', value: apSummary.overdueCount, color: 'text-red-600' },
                { label: 'Payment vouchers', value: apPaymentSummary?.paymentCount ?? '--', color: 'text-slate-700' },
              ].map(({ label, value, color }) => (
                <div key={label} className="flex items-center justify-between py-1.5 border-b border-slate-50">
                  <span className="text-xs text-slate-500">{label}</span>
                  <span className={`text-sm font-semibold ${color}`}>{value}</span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="No AP data available." />
          )}
        </div>
      </div>
    </div>
  );

  const renderRfq = () => (
    <div className="space-y-5">
      <SectionHeader title="RFQ & Inbox" count={rfqStatusCounts.total} onDownload={exportRfq} loading={loading} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total RFQs" value={rfqStatusCounts.total} color="cyan" />
        <StatCard label="Processed" value={rfqStatusCounts.processed} color="cyan" />
        <StatCard label="Pending" value={rfqStatusCounts.pending} color="amber" />
        <StatCard label="Error" value={rfqStatusCounts.error} color="red" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {/* Conversations in the inbox — not emails, and not filtered by classification. */}
        <StatCard label="Inbox conversations" value={inboxCounts?.inbox ?? '--'} />
        <StatCard label="Classified: RFQ" value={inboxCounts?.rfq ?? '--'} color="cyan" />
        <StatCard label="Classified: Quotation" value={inboxCounts?.quotation ?? '--'} color="sky" />
        <StatCard label="Classified: Price List" value={inboxCounts?.price_list ?? '--'} />
        <StatCard label="Classified: Confirmed Order" value={inboxCounts?.confirmed_order ?? '--'} color="cyan" />
        <StatCard label="Classified: Others" value={inboxCounts?.others ?? '--'} />
      </div>

      <SimpleTable
        headers={['Category', 'Count']}
        rows={[
          ['Total RFQs received', rfqStatusCounts.total],
          ['Processed successfully', rfqStatusCounts.processed],
          ['Pending / awaiting', rfqStatusCounts.pending],
          ['Errors', rfqStatusCounts.error],
          ['Inbox conversations', inboxCounts?.inbox ?? '--'],
          ['Classified as RFQ', inboxCounts?.rfq ?? '--'],
          ['Classified as Quotation', inboxCounts?.quotation ?? '--'],
          ['Classified as Confirmed Order', inboxCounts?.confirmed_order ?? '--'],
        ]}
      />
    </div>
  );

  const renderQuotations = () => (
    <div className="space-y-5">
      <SectionHeader title="Quotations" count={quotationStats.total} onDownload={exportQuotations} loading={loading} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Quotations" value={quotationStats.total} color="sky" />
        <StatCard label="Total Value" value={fmt(quotationStats.totalValue, 'USD')} color="cyan" />
        <StatCard label="Avg Value / Quote" value={fmt(quotationStats.avgValue, 'USD')} />
        <StatCard label="Confirmed Orders" value={confirmedStats.total} sub="from quotations" color="cyan" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-2">By status</div>
          <SimpleTable
            headers={['Status', 'Count', '% of Total']}
            rows={Object.entries(quotationStats.byStatus).map(([status, count]) => [
              <span key={status} className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                status === 'sent' ? 'bg-sky-50 text-sky-700' :
                status === 'confirmed' ? 'bg-cyan-50 text-cyan-700' :
                status === 'draft' ? 'bg-slate-100 text-slate-600' :
                status === 'rejected' ? 'bg-red-50 text-red-700' :
                'bg-slate-100 text-slate-600'
              }`}>{status}</span>,
              count,
              `${quotationStats.total ? Math.round(((count as number) / quotationStats.total) * 100) : 0}%`,
            ])}
          />
        </div>
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-2">Top quotations by value</div>
          <SimpleTable
            headers={['#', 'Client', 'Vessel', 'Value (USD)', 'Status']}
            rows={[...quotations]
              .sort((a, b) => (b.summary?.totalValue || 0) - (a.summary?.totalValue || 0))
              .slice(0, 10)
              .map((q, i) => [
                i + 1,
                q.customer?.customerName || '--',
                quotationVesselName(q) || '--',
                fmt(q.summary?.totalValue, 'USD'),
                q.status || '--',
              ])}
          />
        </div>
      </div>
    </div>
  );

  const renderConfirmed = () => (
    <div className="space-y-5">
      <SectionHeader title="Confirmed Orders" count={confirmedStats.total} onDownload={exportConfirmed} loading={loading} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Orders" value={confirmedStats.total} color="cyan" />
        <StatCard label="Total Revenue" value={fmt(confirmedStats.totalRevenue, 'USD')} color="cyan" />
        <StatCard label="Avg Order Value" value={fmt(confirmedStats.avgOrder, 'USD')} />
        <StatCard label="Total Line Items" value={fmt(confirmedStats.totalItems)} />
      </div>

      <SimpleTable
        headers={['#', 'Client', 'Vessel', 'Total Quote', 'Items', 'Confirmed At']}
        rows={confirmedOrders.map((o, i) => [
          i + 1,
          o.clientName || '--',
          o.vesselName || '--',
          fmt(o.totalQuote, o.currency || 'USD'),
          o.totalItems ?? '--',
          fmtDate(o.confirmedAt, '--'),
        ])}
      />
    </div>
  );

  const renderVendorInvoices = () => {
    const stats = vendorInvoiceStats?.stats || {};
    const total = vendorInvoiceStats?.total ?? 0;
    return (
      <div className="space-y-5">
        <SectionHeader title="Vendor Invoices" count={total} onDownload={exportVendorInvoices} loading={loading} />

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Total" value={total} color="default" />
          <StatCard label="Done" value={stats.done ?? '--'} color="cyan" />
          <StatCard label="Pending" value={stats.pending ?? '--'} color="amber" />
          <StatCard label="Failed" value={stats.failed ?? '--'} color="red" />
          <StatCard label="Skipped" value={stats.skipped ?? '--'} color="default" />
        </div>

        <SimpleTable
          headers={['Status', 'Count', '% of Total']}
          rows={Object.entries(stats).map(([status, count]) => [
            <span key={status} className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${
              status === 'done' ? 'bg-cyan-50 text-cyan-700 border-cyan-200' :
              status === 'failed' ? 'bg-amber-50 text-amber-700 border-amber-200' :
              status === 'pending' ? 'bg-sky-50 text-sky-700 border-sky-200' :
              status === 'processing' ? 'bg-amber-50 text-amber-700 border-amber-200' :
              'bg-slate-50 text-slate-600 border-slate-200'
            }`}>{status}</span>,
            count as number,
            `${total ? Math.round(((count as number) / total) * 100) : 0}%`,
          ])}
        />
      </div>
    );
  };

  const renderAP = () => (
    <div className="space-y-5">
      <SectionHeader title="Accounts Payable" count={apSummary?.invoiceCount} onDownload={exportAP} loading={loading} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Invoiced" value={fmt(apSummary?.totalAmount, 'TTD')} color="default" />
        <StatCard label="Paid" value={fmt(apSummary?.paidAmount, 'TTD')} color="cyan" />
        <StatCard label="Outstanding" value={fmt(apSummary?.outstandingAmount, 'TTD')} color="amber" />
        <StatCard label="Overdue" value={apSummary?.overdueCount ?? '--'} sub="invoices past due" color="red" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Unpaid" value={apSummary?.unpaidCount ?? '--'} color="amber" />
        <StatCard label="Partially Paid" value={apSummary?.partialCount ?? '--'} color="sky" />
        <StatCard label="Fully Paid" value={apSummary?.paidCount ?? '--'} color="cyan" />
        <StatCard label="Payment Vouchers" value={apPaymentSummary?.paymentCount ?? '--'} color="purple" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-2">AP status breakdown</div>
          <SimpleTable
            headers={['Status', 'Count', 'Note']}
            rows={[
              ['Unpaid', apSummary?.unpaidCount ?? '--', 'No payment recorded'],
              ['Partially Paid', apSummary?.partialCount ?? '--', 'Some amount paid'],
              ['Fully Paid', apSummary?.paidCount ?? '--', 'Outstanding = 0'],
              ['Overdue', apSummary?.overdueCount ?? '--', 'Past due date'],
            ]}
          />
        </div>
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-2">Payment summary</div>
          <SimpleTable
            headers={['Metric', 'Value']}
            rows={[
              ['Total Payment Vouchers', apPaymentSummary?.paymentCount ?? '--'],
              ['Total Payments Made', fmt(apPaymentSummary?.totalAmount, 'TTD')],
              ['Allocated Amount', fmt(apPaymentSummary?.allocatedAmount, 'TTD')],
              ['Unallocated Amount', fmt(apPaymentSummary?.unallocatedAmount, 'TTD')],
            ]}
          />
        </div>
      </div>
    </div>
  );

  // ── tab content map ───────────────────────────────────────────────────────
  const tabContent: Record<TabId, React.ReactNode> = {
    overview: renderOverview(),
    rfq: renderRfq(),
    quotations: renderQuotations(),
    confirmed: renderConfirmed(),
    vendor_invoices: renderVendorInvoices(),
    ap: renderAP(),
    trace: <SourceTracePanel downloadCsv={downloadCsv} />,
  };

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            {/* The sidebar calls this screen "Reports"; the page now agrees. */}
            <h1 className="text-xl font-semibold leading-7 text-slate-900">Reports</h1>
            <ModuleInfoButton
              content={
                <div className="space-y-2 text-xs">
                  <p>Central hub for all business metrics across modules.</p>
                  <p>Each tab provides stat cards, breakdowns, and CSV export.</p>
                </div>
              }
              ariaLabel="Reports information"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {lastRefreshed && (
              <span className="text-xs text-slate-400">
                Updated {lastRefreshed.toLocaleTimeString()}
              </span>
            )}
            <Button
              onClick={() => void load()}
              loading={loading}
              icon={<RefreshCw aria-hidden="true" className="h-4 w-4" />}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              onClick={exportOverview}
              icon={<Download aria-hidden="true" className="h-4 w-4" />}
            >
              Export all
            </Button>
          </div>
        </div>

        {/* Tab bar. Pressed toggles in a group, not ARIA tabs — the page test
            finds them by button name, which SegmentedControl keeps. */}
        <div className="mt-3 overflow-x-auto">
          <SegmentedControl
            aria-label="Report"
            value={tab}
            onChange={handleTabChange}
            segments={TABS.map((t) => ({ value: t.id, label: t.label }))}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 py-6 overflow-auto sm:px-8">
        {failedModules.length > 0 && (
          <div className="mb-4">
            <ErrorState
              message={`These figures are incomplete: ${failedModules.join(', ')} did not answer.`}
              onRetry={() => void load()}
            />
          </div>
        )}
        {tabContent[tab]}
      </div>
    </div>
  );
};
